
// Api
import { changeJobDetailedMessageVisibility, detailedJobSendMessage } from '@/api';
// Components
import Button from '@/components/UI/Button.vue';
import DateTimezoneToggle from '@/components/UI/dateTimezoneToggle/DateTimezoneToggle.vue';
import Icon from '@/components/UI/Icon.vue';
import Tooltip from '@/components/UI/Tooltip.vue';
import InvoicedJobWarning from '@/components/UI/InvoicedJobWarning.vue';
// Vue
import { mapState } from 'vuex';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as Sentry from '@sentry/vue';
// Types
import { IDetailedJobSendMessage, MessageVisibilityParams, User } from '@/models/main';
import { JobDetailedData, Message } from '@/models/jobDetailed';
// Constants
import { notificationErrorText } from '@/utils/PortalConstants';
// Validation rules:
import { extend } from 'vee-validate';
import { required, max } from 'vee-validate/dist/rules';
extend('required', {
  ...required,
  message: 'Message is required',
});
extend('max', {
  ...max,
  message: 'The message is too long. 250 characters is the limit',
});

@Component({
  components: {
    Button,
    DateTimezoneToggle,
    Icon,
    Notification,
    Tooltip,
    InvoicedJobWarning,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class Messages extends Vue {
  @Prop({ type: Object, required: true }) public readonly job!: JobDetailedData;

  private user: User;
  public message = '';
  public loading = false;
  public loadingVisibility = {};
  public messagesList: Message[] = [];

  @Watch('job.messages', { immediate: true, deep: true })
  private messagesChanged(): void {
    this.messagesList = [];
    this.job.messages.map(message => {
      if (!message.is_private || this.$ability.can('staff', 'Job')) {
        this.messagesList.push(message);
      }
    });
  }

  public togglePrivacy(message): void {
    const showError = () => {
      this.$notify({
        group: 'network',
        title: 'Error changing the message visibility',
        text: notificationErrorText,
        type: 'warn',
      });
    };

    const editingMessage = this.job.messages.find(x => x.message_id === message.message_id);
    const params: MessageVisibilityParams = {
      message_id: message.message_id,
      private: !message.is_private,
    };
    if (editingMessage) {
      this.loadingVisibility[message.message_id] = true;
      changeJobDetailedMessageVisibility(params)
        .then(res => {
          if (res.status === 201) {
            editingMessage.is_private = !message.is_private;
          } else {
            showError();
          }
          this.loadingVisibility[message.message_id] = false;
        })
        .catch(() => {
          showError();
        })
        .finally(() => {
          this.loadingVisibility[message.message_id] = false;
        });
    } else {
      showError();
    }
  }

  public async sendMessage() {
    const showError = res => {
      this.$notify({
        group: 'network',
        title: 'Error sending the message',
        text: notificationErrorText,
        type: 'warn',
      });
      Sentry.withScope(scope => {
        scope.setLevel('error');
        Sentry.captureException(res);
      });
    };
    const form = this.$refs.jobMsgForm as HTMLFormElement;
    if (form) {
      const success = await form.validate();
      if (success) {
        this.loading = true;
        const params: IDetailedJobSendMessage = {
          job_key: this.job.rockhopper_key,
          job_date: this.job.rockhopper_date,
          message: this.message,
          respond_to: this.user.username.slice(0, 20),
          respond_email: this.user.email || '',
        };
        setTimeout(() => {
          detailedJobSendMessage(params)
            .then(res => {
              if (res.Result === 'Message sent') {
                this.$emit('updateJob');
                this.$notify({
                  group: 'network',
                  text: 'The message was sent successfully',
                  type: 'success',
                  duration: 3000,
                });
                this.message = '';
                form.reset();
              } else {
                showError(res);
              }
            })
            .catch(res => {
              showError(res);
            })
            .finally(() => {
              this.loading = false;
            });
        }, 1000);
      }
    }
  }

  private mounted(): void {
    this.job.messages.map(message => {
      this.loadingVisibility[message.message_id] = false;
    });
  }
}
